
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  fetchContactRelationshipHealth,
  updateRelationshipHealth,
  fetchContactInteractions,
  createContactInteraction,
  calculateOrganizationHealthMetrics
} from "@/services/relationship/relationshipHealthService";

export function useRelationshipHealth() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Contact relationship health query
  const getContactHealthQuery = (contactId: string) => useQuery({
    queryKey: ['relationshipHealth', contactId],
    queryFn: () => fetchContactRelationshipHealth(contactId),
    enabled: !!contactId
  });

  // Update relationship health mutation
  const updateRelationshipHealthMutation = useMutation({
    mutationFn: updateRelationshipHealth,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['relationshipHealth', data.contact_id] });
      toast({
        title: "Relationship health updated",
        description: "The relationship health record has been updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to update relationship health",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Contact interactions query
  const getContactInteractionsQuery = (contactId: string) => useQuery({
    queryKey: ['contactInteractions', contactId],
    queryFn: () => fetchContactInteractions(contactId),
    enabled: !!contactId
  });

  // Create contact interaction mutation
  const createInteractionMutation = useMutation({
    mutationFn: createContactInteraction,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contactInteractions', data.contact_id] });
      queryClient.invalidateQueries({ queryKey: ['relationshipHealth', data.contact_id] });
      toast({
        title: "Interaction recorded",
        description: "The interaction has been successfully recorded.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to record interaction",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Organization health metrics query
  const getOrganizationHealthMetricsQuery = (organizationId: string) => useQuery({
    queryKey: ['organizationHealthMetrics', organizationId],
    queryFn: () => calculateOrganizationHealthMetrics(organizationId),
    enabled: !!organizationId
  });

  return {
    getContactHealth: getContactHealthQuery,
    updateContactHealth: updateRelationshipHealthMutation.mutate,
    isUpdatingHealth: updateRelationshipHealthMutation.isPending,
    
    getContactInteractions: getContactInteractionsQuery,
    createInteraction: createInteractionMutation.mutate,
    isCreatingInteraction: createInteractionMutation.isPending,
    
    getOrganizationHealthMetrics: getOrganizationHealthMetricsQuery
  };
}
